import React from "react";
import styled, { useTheme } from "styled-components";
import { GripVertical, X } from "lucide-react";
import IconWithFallback from "Global/IconWithFallback";
import Card from "common/Boxes/Card";
import { NewLookLiveTrendCell } from "features/portfolio/NewLook/NewLookTrendCell.tsx";
import {
  getNewLookTokens,
  getPanelShadow,
  newLookType,
} from "common/NewLook/tokens.ts";
import { NEW_LOOK_ICON_STROKE } from "common/NewLook/layout.ts";
import { MOBILE_MENU, MOBILE_SEARCH } from "common/constants";
import { ICON_TYPES } from "@shared/common/constants";
import { Market } from "types";
import useMedia from "utils/hooks/useMedia";
import useMeasure from "utils/hooks/useMeasure";
import WatchlistPrice from "features/watchlist/common/WatchlistPrice";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import { WATCHLIST_CARD_ID } from "testIds";

interface NewLookSymbolCardProps {
  watchlistMarket: Market;
  onOpen: () => void;
  onRemove: () => void;
}

/**
 * New-look watchlist card: pair and exchange on top, the live price as the
 * hero figure next to a small 24h chart. Remove / drag controls stay quiet
 * until hover on desktop.
 */
const NewLookSymbolCard = ({
  watchlistMarket,
  onOpen,
  onRemove,
}: NewLookSymbolCardProps) => {
  const theme = useTheme();
  const isTablet = useMedia([`(max-width: ${MOBILE_MENU})`], [true], false);
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  // The sparkline spans the card's full width (no empty space beside it).
  const [chartRef, { width: chartWidth }] = useMeasure<HTMLDivElement>();

  return (
    <StyledCard
      onClick={onOpen}
      onLongPress={onRemove}
      data-testid={WATCHLIST_CARD_ID}
    >
      <Header>
        <IconWithFallback
          coinOrExchangeName={watchlistMarket.symbol}
          iconType={ICON_TYPES.COIN}
          pxIconSize={isMobile ? 28 : 36}
        />
        <Names>
          <SymbolName>
            <AutoSizeText inheritFont={true} minFontSizePx={11}>
              {watchlistMarket.symbol}
            </AutoSizeText>
          </SymbolName>
          <Exchange>{watchlistMarket.exchange}</Exchange>
        </Names>
        <Controls $alwaysVisible={isTablet}>
          <IconButton
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <X size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
          </IconButton>
          {!isTablet && (
            <DragHandle onClick={(event) => event.stopPropagation()}>
              <GripVertical size={16} strokeWidth={NEW_LOOK_ICON_STROKE} />
            </DragHandle>
          )}
        </Controls>
      </Header>
      <Body>
        <Price>
          <AutoSizeText inheritFont={true} minFontSizePx={11}>
            <WatchlistPrice
              market={watchlistMarket}
              fontSize={isMobile ? 16 : 20}
              textColor={theme.colors.titleColor}
              showNoneDisplayCurrencyIcons={true}
            />
          </AutoSizeText>
        </Price>
        <Chart ref={chartRef}>
          <NewLookLiveTrendCell
            symbol={watchlistMarket.symbol}
            exchange={watchlistMarket.exchange}
            initialGraphData={watchlistMarket.graph_data}
            yValueIndex={1}
            layout="stacked"
            sparkWidth={Math.max(60, Math.round(chartWidth || 150))}
            sparkHeight={isMobile ? 32 : 40}
            fill={true}
          />
        </Chart>
      </Body>
    </StyledCard>
  );
};

export default NewLookSymbolCard;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  cursor: pointer;
  position: relative;
  border-radius: 12px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  /* Like every card: a mild resting shadow, a little deeper on hover. */
  box-shadow: ${(p) => getPanelShadow(p.theme)};
  transition: box-shadow 0.15s, transform 0.15s;
  &:hover {
    box-shadow: inset 0 0 0 1px
        ${(p) => getNewLookTokens(p.theme).hairlineStrong},
      0 4px 12px rgba(16, 24, 40, 0.08);
    transform: translateY(-1px);
  }
  @media (max-width: ${MOBILE_SEARCH}) {
    gap: 10px;
    padding: 12px;
  }
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const Names = styled.div`
  flex: 1;
  min-width: 0;
`;
const SymbolName = styled.div`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  @media (max-width: ${MOBILE_SEARCH}) {
    font-size: 14px;
  }
`;
const Exchange = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
// Overlays the top-right corner so the name keeps the full width.
const Controls = styled.div<{ $alwaysVisible: boolean }>`
  position: absolute;
  top: 14px;
  right: 12px;
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  opacity: ${(p) => (p.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s;
  ${StyledCard}:hover & {
    opacity: 1;
  }
`;
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  cursor: pointer;
  color: ${(p) => p.theme.colors.secondaryText};
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
    background: ${(p) => p.theme.colors.cardLightBgColor};
  }
`;
const DragHandle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 26px;
  cursor: move;
  color: ${(p) => p.theme.colors.lightestText};
`;
// Price over the chart: side by side they collide on narrow cards.
const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  min-width: 0;
`;
const Price = styled.div`
  min-width: 0;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  /* 16px on phones: an inline value, so 500. */
  @media (max-width: ${MOBILE_SEARCH}) {
    font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  }
  * {
    font-weight: inherit !important;
  }
`;
const Chart = styled.div`
  display: flex;
  min-width: 0;
  > div {
    flex: 1;
    align-items: stretch;
  }
  > div > span {
    text-align: left;
  }
`;
